import { cloneDeep } from 'lodash';
import React, { ReactNode, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CustomBreadCrumbs,
  downloadFile,
  FilterProp,
  renderHeaderActionButtons,
  TableDashboard,
  useAppDispatch,
  useAppSelector,
} from '../../../globalUtils/globalExports';
import {
  exportToCsvForRoleList,
  getFilterSelectedValue,
  setFilterData,
  getCurrentFiltersValues,
  getFilterReducer,
  chargingStationTypes,
  getChargingStationReducer,
} from '../../../rmsReduxStore/reduxExports';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import './ChargingStationEditHistory.css';
import { Id, toast } from 'react-toastify';
import { NoDataComponentUpdateHistory } from '../../../globalUtils/TableDashboard/TableComponents';
import { useLocation } from 'react-router-dom';
import { GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import { createBreadCrumbProps, formatRelativeTimeForTable } from '../../../globalUtils/globalHooks';
import { fetchStationUpdateHistory, setChargingStationTableProps } from '../../../rmsReduxStore/chargingStationRedux/chargingStationCreators';

const EditHistory = memo(function EditHistory() {
  const dispatch = useAppDispatch();
  const {historyRecords,historyRecordCount,historyRecordTableProps,loader} = useAppSelector(getChargingStationReducer)
  const { screenFilters } = useAppSelector(getFilterReducer)
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [exportingFile, setExportingFile] = useState(false);
  const historyRecordTablePropsRef = useRef(historyRecordTableProps);
  const historyRecordsRef = useRef(historyRecords);

  const location = useLocation();

  useEffect(() => {
    historyRecordTablePropsRef.current = historyRecordTableProps;
  }, [historyRecordTableProps]);

  useEffect(() => {
    historyRecordsRef.current = historyRecords;
  }, [historyRecordsRef]);

  const chargingStationCleanUpState = useCallback(() => {
    const action = {
      type: chargingStationTypes.CLEAR_STATION_HISTORY_LIST_DATA,
    }
    dispatch(action);
  }, [])


  const getTableRows = useCallback((): HistoryRecord[] => {
    return historyRecords !== null && historyRecords?.length > 0 ? historyRecords : [];
  }, [historyRecords]);

  useEffect(() => {
    window.addEventListener('beforeunload', chargingStationCleanUpState);
    return (): void => {
      chargingStationCleanUpState()
      window.removeEventListener('beforeunload', chargingStationCleanUpState);
    }
  }, [])

  // TODO: Search Box Configuartions
  const onChangeOfSearchText = useCallback(async (searchTerm) => {
    if (historyRecordTablePropsRef?.current) {
      await layoutCallback(1, historyRecordTablePropsRef?.current?.pageSize, historyRecordTablePropsRef?.current?.view, historyRecordTablePropsRef?.current?.sortBy, historyRecordTablePropsRef?.current?.order, searchTerm, null)
    }
  }, [historyRecordTablePropsRef?.current, historyRecordsRef]);

  const searchBoxConfiguration = useMemo(() => {
    return {
      searchFieldId: 'manage-roles-search-box',
      searchFieldName: 'manage-roles-search-box',
      handleSearch: onChangeOfSearchText,
    };
  }, [onChangeOfSearchText, historyRecordTablePropsRef?.current]);

  // TODO: Adds action btns in header and their callbacks
  const headerActionBtns = renderHeaderActionButtons({
    actionBtns:
      [
        {

          id: 'export_csv_btn',
          headerActionBtnClick: (event): void => {
            setExportAnchorEl(event.currentTarget);

          }
        }
      ]
  })

  // TODO: Table Layout Callbacks
  const mobileViewConfiguration = useMemo(() => {
    return {
      headerDataConfig: {
        headerLeftDataConfig: ['name', 'description'],
        headerRightDataConfig: {
          actionIconsComponent: false,
          statusIconComponent: true,
        },
      },
      statusIconKey: 'status'
    };
  }, []);

  const historyColumns = useMemo(() => {
    return [
      {
        field: 'user',
        headerName: 'User',
        flex: 1,
        sortable: false,
        minWidth: 100,
      },
      {
        field: 'action',
        headerName: 'Actions',
        flex: 1,
        minWidth: 100,
        sortable: false
      },
      {
        field: 'FieldTitle',
        headerName: 'Field',
        flex: 1,
        minWidth: 100,
        sortable: false
      },
      {
        field: 'oldValue',
        headerName: 'Previous Value',
        flex: 1,
        minWidth: 100,
        sortable: false
      },
      {
        field: 'newValue',
        headerName: 'Updated Value',
        flex: 1,
        minWidth: 100,
        sortable: false
      },
      {
        field: 'updatedAt',
        headerName: 'Updated At',
        flex: 1,
        minWidth: 100,
        sortable: true,
        renderCell: (
          params: GridRenderCellParams<GridValidRowModel>
        ): ReactNode => {
          const rowData = params?.row;
          const date = new Date(rowData?.createdAt);
          return formatRelativeTimeForTable(date);
        },
      },
    ];
  }, [historyRecordTableProps]);

  const breadCrumbs = createBreadCrumbProps({
    breadCrumbProps:
      [
        {
          objectType: 'link',
          id: 'chargingStation',
     
        },
        {
          objectType: 'link',
          id: 'editChargingStation',
          linkBtnState: location?.state,
        },
        {
          objectType: 'text',
          id: 'updateHistory',
          text: 'Update History'
        }
      ]
  })

  const handleFilterDataChange = useCallback(async (val, filterKey, isGlobal) => {

    const tableProps = cloneDeep(historyRecordTablePropsRef.current);
    const res = await setFilterData(val, filterKey, 'chargingStationEditHistory', 'SET', isGlobal ? isGlobal : false, true)(dispatch);
    if (res?.message === 'Action dispatched successfully') {
      layoutCallback?.(
        1,
        tableProps?.pageSize,
        tableProps?.view ?? '',
        tableProps?.sortBy,
        tableProps?.order,
        tableProps?.searchTerm ?? '',
        res?.filters
      )
    }
  }, [historyRecordTablePropsRef.current])

  const chargingStationFilterProps: FilterProp[] = useMemo(() => {
    const updatedTimeStampValue = getFilterSelectedValue('stationUpdateInterval', 'chargingStationEditHistory', false);
    return [
      {
        filterLabel: 'Time (Updated At)',
        filterType: 'dateFilter',
        filterId: 'stationUpdateInterval',
        dateFilterProps: {
          datePickerId: 'alarm__time__stamp__set__at__date__picker',
          datePickerClassName: '',
          selectsRange: true,
          startDate: updatedTimeStampValue?.[0] ? new Date(updatedTimeStampValue?.[0]) : null,
          endDate: updatedTimeStampValue?.[1] ? new Date(updatedTimeStampValue?.[1]) : null,
          onChange: (val): void => {
            handleFilterDataChange(val, 'stationUpdateInterval', false)
          },
        }
      },

    ]
  }, [screenFilters])


  const layoutCallback = useCallback(async (pageNumber: number, pageSize: number, view: string, sortField: string, sortOrder: string, searchTerm, filterData) => {
    const tableProps: IListTableProps = cloneDeep(historyRecordTablePropsRef.current)
    const filters = filterData ? filterData : getCurrentFiltersValues('chargingStationEditHistory', true)

    await dispatch(fetchStationUpdateHistory({
      chargingStationId: location?.state?.id,
      sortBy: sortField || '',
      order: sortOrder || 'ascending',
      filters: filters,
      pageSize,
      pageNumber,
      searchTerm: searchTerm != null && searchTerm != undefined ? searchTerm : historyRecordTablePropsRef?.current?.searchTerm
    }));
    if (tableProps) {
      tableProps.view = view || 'list'
      tableProps.sortBy = sortField || ''
      tableProps.order = sortOrder || 'ascending';
      tableProps.pageNumber = pageNumber;
      tableProps.pageSize = pageSize;
      if (searchTerm != null && searchTerm != undefined) {
        tableProps.searchTerm = searchTerm;
      }
    }
    await dispatch(setChargingStationTableProps(tableProps))
  }, [historyRecordTablePropsRef?.current, dispatch]);

  // TODO: Redux state Clean up
  const editHistoryCleanUpStates = useCallback(() => {
    // : Partial<UserAction>
    const action = {
      type: chargingStationTypes.CLEAR_STATION_HISTORY_LIST_DATA,
    }
    dispatch(action);
  }, [])

  useEffect(() => {
    window.addEventListener('beforeunload', editHistoryCleanUpStates);
    return (): void => {
      editHistoryCleanUpStates()
      window.removeEventListener('beforeunload', editHistoryCleanUpStates);
    }
  }, [])

  // ***********************************************************
  // TODO: Export Handlers
  const handleClose = (): void => {
    setExportAnchorEl(null);
  };

  const fileDownloadSuccessToast = useCallback((): Id => {
    setExportingFile(false);
    return toast.success('File downloaded.');
  }, []);

  const fileDownloadFailureToast = useCallback((): Id => {
    setExportingFile(false);
    return toast.warn('Something went wrong. Please export again.');
  }, []);


  const onExportBtnClick = useCallback(async (fileType: 'csv' | 'xlsx'): Promise<void> => {
    if (exportingFile) {
      // If already exporting, prevent another export
      return;
    }
    try {
      const response = await dispatch(exportToCsvForRoleList({
        fileType: fileType,
        filters: getCurrentFiltersValues('chargingStationEditHistory', true)
      }));
      if (response?.status === 202 || response?.status === 200) {
        downloadFile({ url: response?.data?.url });
        fileDownloadSuccessToast()
      } else {
        fileDownloadFailureToast();
        setExportAnchorEl(null);
      }
    } finally {
      // Ensure that exporting status is updated even if there's an error
      setExportingFile(false);
      setExportAnchorEl(null);
    }
  }, [exportingFile, fileDownloadSuccessToast, fileDownloadFailureToast])

  return (
    <>
      <div className='edit__history__wrap'>
        <div className='edit__history__wrap-header-link-row'>
          <CustomBreadCrumbs breadCrumbs={breadCrumbs} />
        </div>
        <TableDashboard
          tableId="chargingStation-history-table"
          tableClassName="chargingStation-history-table"
          header={'Update History'}
          searchBoxIncluded={true}
          searchBoxConfiguration={searchBoxConfiguration}
          gridColumns={historyColumns}
          tableRows={getTableRows()}
          mobileViewConfiguration={mobileViewConfiguration}
          layoutView={'list'}
          headerActionBtns={headerActionBtns}
          listLayoutCallBack={layoutCallback}
          totalCount={historyRecordCount}
          showSkeleton={!historyRecords}
          totalCountText={'updates'}
          showLoader={loader}
          customNoDataComponent={NoDataComponentUpdateHistory}
          pageNumber={historyRecordTableProps?.pageNumber}
          tablePageSize={historyRecordTableProps?.pageSize}
          excludeGlobalFilters={true}
          filterProps={chargingStationFilterProps}
          handleFilterDataChange={handleFilterDataChange}
        />
        <Menu
          id="export-option-dropdown"
          anchorEl={exportAnchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          className='export__option__dropdown'
          sx={{ top: '30px' }}
          open={Boolean(exportAnchorEl)}
          onClose={handleClose}

        >
          <MenuItem>
            <div className='export__btn-option' onClick={(e) => {
              e?.stopPropagation();
              onExportBtnClick('csv')
            }}>
              To CSV
            </div>
          </MenuItem>
          <MenuItem>

            <div className='export__btn-option' onClick={(e) => {
              e?.stopPropagation();
              onExportBtnClick('xlsx')
            }}>
              To Excel
            </div>
          </MenuItem>
        </Menu>
      </div>
    </>
  );
})

export default EditHistory;
